import {
  createMemoryInputSchema,
  doodleStrokeSchema,
  mediaAssetSchema,
  memorySchema,
  type MemoryWithMedia
} from "@memory-universe/domain";
import { supabase } from "../../../shared/supabase/client";
import { useSpaceStore } from "../../spaces/model/spaceStore";
import {
  getMediaUrl,
  type MediaStorageProvider
} from "./cloudMediaStorage";
import type { CreateMemoryCommand, MemoryRepository, RecentlyDeletedItem } from "./memoryRepository";
import { readLocalMediaPreviewUrl } from "../../../shared/platform/localOriginalMedia";
import { probeRuntime } from "../../../shared/platform/runtime";
import { registerRuntimeDevice } from "../../devices/data/deviceRegistrationRepository";
import {
  stageEncryptedMediaTransfer,
  uploadStagedEncryptedMediaTransfer
} from "../../devices/data/encryptedMediaRelayRepository";
import { scheduleV2DepthGeneration } from "../../devices/data/v2DepthGenerationService";

type HydratedMedia = MemoryWithMedia["media"];

interface MediaRow {
  id: string;
  memory_id: string;
  space_id: string;
  owner_id: string;
  storage_provider: string | null;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size: number;
  created_at: string;
  captured_at?: string | null;
  allow_comments?: boolean | null;
  allow_doodles?: boolean | null;
}

function client() {
  if (!supabase) throw new Error("Supabase 尚未配置");
  return supabase;
}

export function assembleVisibleMemory(
  row: Record<string, unknown>,
  media: HydratedMedia,
  canViewWithoutMedia = false
): MemoryWithMedia | null {
  if (media.length === 0 && !canViewWithoutMedia) return null;
  const memory = memorySchema.parse({
    id: row.id,
    spaceId: row.space_id,
    title: row.title,
    body: row.body,
    occurredAt: row.occurred_at,
    mood: row.mood,
    mediaIds: media.map((item) => item.id),
    doodleStrokes: Array.isArray(row.doodle_strokes) ? row.doodle_strokes.map((stroke) => doodleStrokeSchema.parse(stroke)) : [],
    createdBy: row.created_by,
    updatedBy: row.updated_by,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at
  });
  return { memory, media };
}

async function hydrate(row: Record<string, unknown>): Promise<MemoryWithMedia | null> {
  const database = client();
  const memoryId = String(row.id);
  const detailedResult = await database
    .from("media_assets")
    .select("id, memory_id, space_id, owner_id, storage_provider, storage_path, file_name, mime_type, size, created_at, captured_at, allow_comments, allow_doodles")
    .eq("memory_id", memoryId)
    .is("deleted_at", null)
    .order("created_at");
  let mediaRows = detailedResult.data as MediaRow[] | null;
  if (detailedResult.error) {
    // A browser running against a project that has not yet received the
    // interaction migration must still be able to read its older memories.
    const message = `${detailedResult.error.code ?? ""} ${detailedResult.error.message ?? ""}`;
    if (!/captured_at|allow_comments|allow_doodles|PGRST204|42703/i.test(message)) throw detailedResult.error;
    const legacyResult = await database
      .from("media_assets")
      .select("id, memory_id, space_id, owner_id, storage_provider, storage_path, file_name, mime_type, size, created_at")
      .eq("memory_id", memoryId)
      .is("deleted_at", null)
      .order("created_at");
    if (legacyResult.error) throw legacyResult.error;
    mediaRows = legacyResult.data as MediaRow[] | null;
  }
  const media = await Promise.all((mediaRows ?? []).map(async (mediaRow) => {
    const metadata = mediaAssetSchema.parse({
      id: mediaRow.id,
      memoryId: mediaRow.memory_id,
      ownerId: mediaRow.owner_id,
      fileName: mediaRow.file_name,
      mimeType: mediaRow.mime_type,
      size: mediaRow.size,
      createdAt: mediaRow.created_at,
      capturedAt: mediaRow.captured_at ?? mediaRow.created_at,
      allowComments: mediaRow.allow_comments ?? true,
      allowDoodles: mediaRow.allow_doodles ?? true
    });
    const provider = (mediaRow.storage_provider ?? "supabase") as MediaStorageProvider;
    let localUrl: string | null = null;
    try {
      localUrl = await readLocalMediaPreviewUrl(metadata.id);
    } catch {
      // A derived local URL is an optimization. A corrupt or missing device
      // cache must not hide the memory metadata from its authorized members.
    }
    let remoteUrl = "";
    if (!localUrl && provider !== "device") {
      try {
        remoteUrl = await getMediaUrl(provider, mediaRow.space_id, mediaRow.storage_path);
      } catch {
        // Storage retention can race a local download. Keep the memory visible
        // and let the encrypted sync layer restore the image when possible.
      }
    }
    return {
      ...metadata,
      url: localUrl ?? remoteUrl
    };
  }));
  return assembleVisibleMemory(row, media, useSpaceStore.getState().user?.id === row.created_by);
}

class SupabaseMemoryRepository implements MemoryRepository {
  async list(galaxyId: string) {
    const { data, error } = await client().from("memories").select("*").eq("space_id", galaxyId).is("deleted_at", null).order("occurred_at");
    if (error) throw error;
    const memories = await Promise.all((data ?? []).map(async (row) => {
      try {
        return await hydrate(row);
      } catch {
        // A damaged or no-longer-authorized media row must not prevent the
        // rest of an authorized galaxy from loading.
        return null;
      }
    }));
    return memories.filter((memory): memory is MemoryWithMedia => memory !== null);
  }

  async get(memoryId: string) {
    const { data, error } = await client().from("memories").select("*").eq("id", memoryId).is("deleted_at", null).maybeSingle();
    if (error) throw error;
    return data ? hydrate(data) : null;
  }

  async create(command: CreateMemoryCommand) {
    const database = client();
    const input = createMemoryInputSchema.parse(command.input);
    const { user } = useSpaceStore.getState();
    if (!user) throw new Error("请先进入一条记忆星河");
    const runtime = await probeRuntime();
    if (!runtime.storageReady || !runtime.deviceId) {
      throw new Error("这台设备的安全本地存储尚未准备好");
    }
    await registerRuntimeDevice(user.id, runtime);
    const memoryId = crypto.randomUUID();
    let relayJobsCreated = false;
    try {
      const { data: memoryRow, error: memoryError } = await database.from("memories").insert({
        id: memoryId, space_id: command.galaxyId, title: input.title, body: input.body,
        occurred_at: input.occurredAt, mood: input.mood, doodle_strokes: input.doodleStrokes,
        created_by: user.id, updated_by: user.id
      }).select("*").single();
      if (memoryError) throw memoryError;
      const media = [];
      for (const { id: mediaId, file } of command.media) {
        const storagePath = `${command.galaxyId}/${memoryId}/${mediaId}.device`;
        media.push({ mediaId, storagePath, provider: "device" as const, file });
      }
      const { error: mediaError } = await database.from("media_assets").insert(media.map(({ mediaId, storagePath, provider, file }) => ({
        id: mediaId, memory_id: memoryId, space_id: command.galaxyId, owner_id: user.id,
        storage_provider: provider, storage_path: storagePath, file_name: file.name, mime_type: file.type, size: file.size,
        captured_at: input.occurredAt, allow_comments: true, allow_doodles: true
      })));
      if (mediaError) throw mediaError;
      for (const { mediaId } of media) {
        const snapshotResult = await database.rpc("snapshot_media_targets", { target_media_id: mediaId });
        if (snapshotResult.error && snapshotResult.error.code !== "PGRST202") throw snapshotResult.error;
      }
      const transfers = [];
      for (const { mediaId } of media) {
        transfers.push(await stageEncryptedMediaTransfer(command.galaxyId, mediaId, runtime));
      }
      relayJobsCreated = true;
      const created = await hydrate(memoryRow);
      if (!created) throw new Error("记忆媒体发布后仍不可见");
      for (const transfer of transfers) {
        void uploadStagedEncryptedMediaTransfer(transfer).catch(() => undefined);
      }
      for (const { mediaId } of media) {
        scheduleV2DepthGeneration({ galaxyId: command.galaxyId, mediaId, runtime });
      }
      return created;
    } catch (error) {
      if (!relayJobsCreated) {
        await database.rpc("soft_delete_memory", { target_memory_id: memoryId });
      }
      throw error;
    }
  }

  async softDeleteMemory(memoryId: string) {
    const { error } = await client().rpc("soft_delete_memory", { target_memory_id: memoryId });
    if (error) throw error;
  }

  async softDeleteMemoryMedia(memoryId: string, mediaId: string) {
    const { error } = await client().rpc("soft_delete_memory_media", {
      target_memory_id: memoryId,
      target_media_id: mediaId
    });
    if (error) throw error;
  }

  async listRecentlyDeleted(galaxyId: string) {
    const database = client();
    const [memoryResult, mediaResult] = await Promise.all([
      database.rpc("list_recently_deleted_memories", { target_space_id: galaxyId }),
      database.rpc("list_recently_deleted_media", { target_space_id: galaxyId })
    ]);
    if (memoryResult.error) throw memoryResult.error;
    if (mediaResult.error) throw mediaResult.error;

    const memories: RecentlyDeletedItem[] = (memoryResult.data ?? []).map((row: Record<string, unknown>) => {
      const memory = memorySchema.parse({
        id: row.memory_id,
        spaceId: row.space_id,
        title: row.title,
        body: row.body,
        occurredAt: row.occurred_at,
        mood: row.mood,
        mediaIds: [],
        doodleStrokes: Array.isArray(row.doodle_strokes) ? row.doodle_strokes.map((stroke: unknown) => doodleStrokeSchema.parse(stroke)) : [],
        createdBy: row.created_by,
        updatedBy: row.updated_by,
        version: row.version,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        deletedAt: row.deleted_at
      });
      return {
        kind: "memory",
        memory,
        deletedAt: memory.deletedAt ?? String(row.deleted_at),
        mediaCount: Number(row.media_count)
      };
    });
    const media: RecentlyDeletedItem[] = (mediaResult.data ?? []).map((row: Record<string, unknown>) => ({
      kind: "media",
      memoryId: String(row.memory_id),
      memoryTitle: String(row.memory_title),
      occurredAt: String(row.occurred_at),
      media: mediaAssetSchema.parse({
        id: row.media_id,
        memoryId: row.memory_id,
        ownerId: row.owner_id,
        fileName: row.file_name,
        mimeType: row.mime_type,
        size: Number(row.size),
        createdAt: row.media_created_at
      }),
      deletedAt: String(row.media_deleted_at)
    }));
    return [...memories, ...media].toSorted((left, right) => Date.parse(right.deletedAt) - Date.parse(left.deletedAt));
  }

  async restoreMemory(memoryId: string) {
    const { error } = await client().rpc("restore_memory", { target_memory_id: memoryId });
    if (error) throw error;
  }

  async restoreMemoryMedia(memoryId: string, mediaId: string) {
    const { error } = await client().rpc("restore_memory_media", {
      target_memory_id: memoryId,
      target_media_id: mediaId
    });
    if (error) throw error;
  }
}

export const supabaseMemoryRepository: MemoryRepository = new SupabaseMemoryRepository();
